import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IReduxState, AppDispatch } from 'app/store';
import { Grid } from 'common/components/grid/Grid';
import { SubsidiariesActions } from './SubsidiariesActions/SubsidiariesActions';
import { SubsidiariesTreeView } from './SubsidiariesTreeView/SubsidiariesTreeView';
import { RestApi } from 'common/services/rest-api.service';
import { ColumnsManage } from 'common/components/grid/columnsManage';
import { IRootColumnPicker } from 'common/interfaces/interface';
import { setFields, setInitialPath, removeAllRows, setDeletePromptParams } from 'features/GridSlice';
import { setColumnPicker, setShowColumnPicker } from 'features/columnPickerSlice';
import {
  fetchAllSubsidiaries,
  resetSubsidiaryState,
  setRefreshSubsidiariesTreeView,
  setSubsidiariesEditMode,
  setSubsidiarySelectedId,
  toggleSubsidiariesPopup
} from 'features/SubsidiariesSlice';
import GroupsPopside from 'pages/Settings/FilterGroups/GroupsPopside';
import { PopSide } from 'common/components/PopSide/PopSide';
import { resetGroupsState } from 'features/GroupsSlice';

import './Subsidiaries.scss';

export enum ViewType {
  GRID = 'grid',
  TREE = 'tree'
}

export function Subsidiaries() {
  const dispatch: AppDispatch = useDispatch();
  const [viewType, setViewType] = useState<ViewType>(ViewType.GRID);
  const { rows, count } = useSelector((state: IReduxState) => state.grid);
  const { accountName } = useSelector((state: IReduxState) => state.login);
  const { subsidiariesPopupVisible, refreshSubsidiariesTreeView } = useSelector(
    (state: IReduxState) => state.subsidiaries
  );
  const [gridData, setGridData] = useState<{
    columns: any[];
    identifier: string;
    isMoreButton: boolean;
    isCSVDownloadable: boolean;
    rootPath: string;
  }>({
    columns: [],
    identifier: '',
    isMoreButton: false,
    isCSVDownloadable: false,
    rootPath: ''
  });

  const handleViewChange = (type: ViewType) => {
    setViewType(type);
  };

  const getColumnsPath = () => {
    return 'settings/organization-management/subsidiaries/columns/';
  };

  const getRowsPath = () => {
    return 'settings/organization-management/subsidiaries/';
  };

  const handleMenuItemClick = (rowData: any, option: any, type: string, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (option.id === 'delete') {
      dispatch(setSubsidiarySelectedId(rowData.id));

      dispatch(
        setDeletePromptParams({
          path: 'settings/organization-management/subsidiaries/',
          index: null,
          ids: [rowData.id],
          message: `Are you sure you would like to remove this subsidiary?`
        })
      );
    }
    if (option.id === 'edit') {
      dispatch(setSubsidiariesEditMode(true));
      dispatch(setSubsidiarySelectedId(rowData.id));
      dispatch(toggleSubsidiariesPopup(true));
    }
  };

  const getData = () => {
    dispatch(removeAllRows());

    RestApi.getData(getColumnsPath()).subscribe((columns: IRootColumnPicker) => {
      let fields = ColumnsManage.checkGridColumnsToDisplay(columns.data);
      dispatch(setFields(fields));
      dispatch(setInitialPath(getRowsPath()));
      dispatch(setColumnPicker(columns.data));
      dispatch(setShowColumnPicker(columns.is_displayable));

      setGridData({
        columns: ColumnsManage.createColumns(
          columns.identifier_field,
          columns.data || [],
          {
            page: 'settings',
            category: 'organization-management',
            tab: 'subsidiaries'
          },
          { menuItemOnClick: handleMenuItemClick }
        ),
        identifier: columns.identifier_field,
        isMoreButton: false,
        isCSVDownloadable: columns.is_csv_downloadable,
        rootPath: getRowsPath()
      });
    });
  };

  useEffect(() => {
    getData();
    dispatch(fetchAllSubsidiaries());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refreshSubsidiariesTreeView) {
      dispatch(setRefreshSubsidiariesTreeView(false));
      dispatch(fetchAllSubsidiaries());
    }
  }, [refreshSubsidiariesTreeView, dispatch]);

  const setPopupVisible = (b: boolean) => {
    if (!b) {
      dispatch(resetSubsidiaryState());
      dispatch(resetGroupsState());
    }
    dispatch(toggleSubsidiariesPopup(b));
  };

  return (
    <div className="subsidiaries-container" style={{ height: '100%' }}>
      <div className="view-tabs">
        <div className="tabs-container">
          <div
            className={`tab ${viewType === ViewType.GRID ? 'active' : ''}`}
            onClick={() => handleViewChange(ViewType.GRID)}
          >
            Grid View
          </div>
          <div
            className={`tab ${viewType === ViewType.TREE ? 'active' : ''}`}
            onClick={() => handleViewChange(ViewType.TREE)}
          >
            Tree View
          </div>
        </div>
      </div>

      {viewType === ViewType.TREE ? (
        <>
          <SubsidiariesTreeView accountName={accountName} />
        </>
      ) : (
        <div style={{ height: 'calc(100% - 60px)', zIndex: 1, marginTop: '16px' }}>
          {gridData.columns.length > 0 && (
            <>
              <Grid
                columns={gridData.columns}
                rows={rows}
                count={count}
                path={gridData.rootPath}
                isMoreButton={gridData.isMoreButton}
                isCSVDownloadable={gridData.isCSVDownloadable}
                addedActions={(props: any) => <SubsidiariesActions {...props} />}
                identifier={gridData.identifier}
                initialState={{
                  pinnedColumns: { right: ['actions'] }
                }}
              />
            </>
          )}
        </div>
      )}
      {subsidiariesPopupVisible && (
        <div className="groups-popside">
          <PopSide visible={subsidiariesPopupVisible} onHide={() => setPopupVisible(false)}>
            <GroupsPopside onHide={() => setPopupVisible(false)} source="Subsidiary" />
          </PopSide>
        </div>
      )}
    </div>
  );
}

export default Subsidiaries;
